import Link from 'next/link'
import Image from 'next/image'
import { ConditionalWrapper, Heading, PortalModal, Form, Button, Paragraph } from '~elements'
import styles from './ExperienceCard.module.scss'
import { AssetProps, ButtonProps, RichTextType } from '~types'
import { getGtmLinkPosition } from '~analyticsUtils'
import { useState } from 'react'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { getImageUrl } from 'utils/assets/getImageUrl'
import { useWindowSize } from 'utils/hooks'

interface ExperienceCardProps {
  imageRedirectUrl?: string
  buttonGroup?: Array<ButtonProps>
  headline?: string
  eyebrow?: string
  description?: RichTextType
  image?: AssetProps
}

const ExperienceCard = ({
  buttonGroup,
  headline,
  eyebrow,
  description,
  imageRedirectUrl = '',
  image
}: ExperienceCardProps) => {
  const [showSignUpModal, setShowSignUpModal] = useState(false)
  const [modalFormId, setModalFormId] = useState('')

  const { isBreakpoint: isBelowLg } = useWindowSize('lg', '<')

  const linkModule = 'card'
  const imageUrl = getImageUrl({ imageUrl: image?.url })

  const handleShowSignUpModal = (formID: string) => {
    if (formID) {
      setShowSignUpModal(true)
      setModalFormId(formID)
    }
  }

  const closeModalHandler = () => {
    setShowSignUpModal(false)
    setModalFormId('')
  }

  const formSubmitHandler = () => {
    setShowSignUpModal(false)
    setModalFormId('')
  }

  return (
    <>
      <div className={styles['experience-card']}>
        <div className={styles['image-container']}>
          {imageUrl && (
            <ConditionalWrapper
              condition={imageRedirectUrl.length > 0}
              wrapper={children => (
                <Link href={imageRedirectUrl} target='_blank' passHref>
                  {children}
                </Link>
              )}
            >
              <Image alt={image?.altText ?? headline ?? ''} src={imageUrl} fill quality={NEXT_IMAGE_QUALITY} />
            </ConditionalWrapper>
          )}
        </div>
        <div className={styles['experience-card-content']}>
          <div className={styles['experience-card-content__inner']}>
            {headline && <Heading level={2}>{headline}</Heading>}
            {eyebrow && (
              <Heading level={6} levelDisplay={'eyebrow'} color={'gold'}>
                {eyebrow}
              </Heading>
            )}
            {description && <Paragraph text={description} margin={'none'} />}
          </div>
        </div>
        {buttonGroup && buttonGroup.length > 0 && (
          <div className={styles['btn-container']}>
            {buttonGroup.map((button, index) => (
              <Button
                key={`${button.title}_${index}`}
                {...button}
                isFullWidth={isBelowLg}
                {...(button?.modalFormId && {
                  onClick: () => handleShowSignUpModal(button.modalFormId ?? '')
                })}
                analytics={{
                  linkModule: linkModule,
                  clickTracking: `${linkModule}:${headline}:${button.title}:cta`,
                  linkPosition: getGtmLinkPosition(buttonGroup, index)
                }}
              />
            ))}
          </div>
        )}
      </div>
      <PortalModal isOpen={showSignUpModal} handleModalClose={closeModalHandler} closeOnOutsideClick={false}>
        <Form formId={modalFormId} formSubmitHandler={formSubmitHandler} isChildOfModal />
      </PortalModal>
    </>
  )
}

export default ExperienceCard
